class QuantityStepper extends HTMLElement {
  constructor() {
    super();
    this.input = null;
    this.decreaseBtn = null;
    this.increaseBtn = null;
  }

  connectedCallback() {
    this.input = this.querySelector('.buyback-form__stepper-input');
    this.decreaseBtn = this.querySelector('[data-action="decrease"]');
    this.increaseBtn = this.querySelector('[data-action="increase"]');
    
    if (!this.input || !this.decreaseBtn || !this.increaseBtn) return;
    
    this.addEventListeners();
    this.updateButtonStates();
  }

  addEventListeners() {
    this.decreaseBtn.addEventListener('click', () => this.decrease());
    this.increaseBtn.addEventListener('click', () => this.increase());
    
    // Allow manual input
    this.input.addEventListener('input', () => this.handleManualInput());
  }

  decrease() {
    const currentValue = parseInt(this.input.value) || 0;
    if (currentValue > 0) {
      this.input.value = currentValue - 1;
      this.updateButtonStates();
      this.dispatchChangeEvent();
    }
  }

  increase() {
    const currentValue = parseInt(this.input.value) || 0;
    this.input.value = currentValue + 1;
    this.updateButtonStates();
    this.dispatchChangeEvent();
  }

  handleManualInput() {
    let value = parseInt(this.input.value) || 0;
    
    // Ensure value is not negative
    if (value < 0) value = 0;
    
    this.input.value = value;
    this.updateButtonStates();
    this.dispatchChangeEvent();
    
    // Clear any custom validation message when value changes
    this.input.setCustomValidity('');
  }

  updateButtonStates() {
    const currentValue = parseInt(this.input.value) || 0;
    
    // Disable decrease button if value is 0
    if (currentValue === 0) {
      this.decreaseBtn.disabled = true;
      this.decreaseBtn.setAttribute('aria-disabled', 'true');
    } else {
      this.decreaseBtn.disabled = false;
      this.decreaseBtn.setAttribute('aria-disabled', 'false');
    }
  }

  dispatchChangeEvent() {
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

// Define the custom element
if (!customElements.get('quantity-stepper')) {
  customElements.define('quantity-stepper', QuantityStepper);
}

// Form validation and submission handling
class BuybackForm {
  constructor() {
    this.form = document.querySelector('.buyback-form__form');
    if (this.form) {
      this.init();
    }
  }

  init() {
    // Add custom validation for quantity inputs
    this.addQuantityValidation();
    
    this.form.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      // Check if form passes HTML5 validation
      if (!this.form.checkValidity()) {
        // Trigger browser's native validation UI
        this.form.reportValidity();
        return;
      }
      
      // Custom validation: at least one silver quantity must be selected
      const silverInputs = this.form.querySelectorAll('.buyback-form__stepper-input');
      let totalQuantity = 0;
      
      silverInputs.forEach(input => {
        totalQuantity += parseInt(input.value) || 0;
      });
      
      if (totalQuantity === 0) {
        // Find the first stepper input and set a custom validation message
        const firstStepperInput = silverInputs[0];
        firstStepperInput.setCustomValidity('Please select at least one quantity of silver bars to sell.');
        this.form.reportValidity();
        firstStepperInput.setCustomValidity(''); // Reset for next validation
        return;
      }
      
      // Get form data
      const formData = new FormData(this.form);
      
      // Combine phone number with country code
      const countryCode = formData.get('country_code') || '+44';
      const phoneNumber = formData.get('phone_number') || '';
      formData.set('phone', `${countryCode}${phoneNumber}`.trim());
      
      // Remove the separate fields
      formData.delete('country_code');
      formData.delete('phone_number');
      
      // Convert terms_accepted checkbox to proper format
      if (formData.has('terms_accepted')) {
        formData.set('terms_accepted', 'Yes');
      }
      
      // Disable submit button to prevent multiple submissions
      const submitButton = this.form.querySelector('.buyback-form__submit');
      const originalButtonText = submitButton.textContent;
      submitButton.disabled = true;
      submitButton.textContent = 'Submitting...';
      
      try {
        // Submit to Formspree
        const response = await fetch(this.form.action, {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (response.ok) {
          // Reset form
          this.form.reset();
          
          // Reset all steppers to 0
          silverInputs.forEach(input => {
            input.value = '0';
            const stepper = input.closest('quantity-stepper');
            if (stepper) {
              stepper.updateButtonStates();
            }
          });
          
          // Show success dialog
          const successDialog = document.querySelector('buyback-success-dialog');
          if (successDialog) {
            successDialog.open();
          }
        } else {
          // Handle error
          alert('There was an error submitting your request. Please try again or contact support.');
        }
      } catch (error) {
        console.error('Form submission error:', error);
        alert('There was an error submitting your request. Please check your connection and try again.');
      } finally {
        // Re-enable submit button
        submitButton.disabled = false;
        submitButton.textContent = originalButtonText;
      }
    });
  }
  
  addQuantityValidation() {
    // Add validation to ensure at least one quantity is selected
    const silverInputs = this.form.querySelectorAll('.buyback-form__stepper-input');
    
    silverInputs.forEach(input => {
      // Remove the readonly attribute to allow validation
      input.removeAttribute('readonly');
      
      // Add input event to clear custom validity when user changes value
      input.addEventListener('input', () => {
        // Clear any custom validation message
        input.setCustomValidity('');
        
        // Ensure the value is not negative
        if (parseInt(input.value) < 0) {
          input.value = 0;
        }
      });
      
      // Prevent manual typing (keep the stepper behavior)
      input.addEventListener('keydown', (e) => {
        e.preventDefault();
      });
    });
  }
}

// Success Dialog Web Component
class BuybackSuccessDialog extends HTMLElement {
  constructor() {
    super();
    this.overlay = null;
    this.content = null;
    this.closeButton = null;
    this.isOpen = false;
  }

  connectedCallback() {
    this.overlay = this.querySelector('.buyback-success-dialog__overlay');
    this.content = this.querySelector('.buyback-success-dialog__content');
    this.closeButton = this.querySelector('.buyback-success-dialog__close');
    
    if (!this.overlay || !this.content || !this.closeButton) return;
    
    this.addEventListeners();
  }

  addEventListeners() {
    // Close on overlay click
    this.overlay.addEventListener('click', () => this.close());
    
    // Close on close button click
    this.closeButton.addEventListener('click', () => this.close());
    
    // Close on escape key
    this.handleEscape = (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    };
    
    // Prevent content clicks from closing
    this.content.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }

  open() {
    this.removeAttribute('hidden');
    this.isOpen = true;
    document.addEventListener('keydown', this.handleEscape);
    
    // Focus management
    this.previousFocus = document.activeElement;
    this.closeButton.focus();
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.setAttribute('hidden', '');
    this.isOpen = false;
    document.removeEventListener('keydown', this.handleEscape);
    
    // Restore focus
    if (this.previousFocus) {
      this.previousFocus.focus();
    }
    
    // Restore body scroll
    document.body.style.overflow = '';
  }
}

// Define the dialog custom element
if (!customElements.get('buyback-success-dialog')) {
  customElements.define('buyback-success-dialog', BuybackSuccessDialog);
}

// Initialize form when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new BuybackForm();
});